import { Fragment, type ReactNode } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/cn';

export interface DropdownMenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  /** Renders in the "missed" red. Pair with a confirm dialog for anything irreversible. */
  destructive?: boolean;
  disabled?: boolean;
  /** Draw a divider above this item. */
  separatorBefore?: boolean;
}

export interface DropdownMenuProps {
  /**
   * The element that opens the menu. Rendered with `asChild`, so it must be a
   * single element that accepts a ref and spreads props — <IconButton> and
   * <Button> both do.
   */
  trigger: ReactNode;
  items: DropdownMenuItem[];
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  /** Announced to screen readers when the menu opens. */
  label?: string;
}

export function DropdownMenu({
  trigger,
  items,
  align = 'end',
  side = 'bottom',
  className,
  label,
}: DropdownMenuProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>{trigger}</DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={12}
          aria-label={label}
          className={cn(
            'z-50 min-w-48 overflow-hidden rounded-lg border border-line bg-surface p-1 text-ink shadow-pop',
            'data-[state=open]:animate-[medtrack-fade-in_150ms_ease-out]',
            'data-[state=closed]:animate-[medtrack-fade-out_150ms_ease-out]',
            className,
          )}
        >
          {items.map((item, index) => (
            <Fragment key={`${item.label}-${index}`}>
              {item.separatorBefore && index > 0 ? (
                <DropdownMenuPrimitive.Separator className="my-1 h-px bg-line" />
              ) : null}
              <DropdownMenuPrimitive.Item
                disabled={item.disabled}
                // Radix calls onSelect with an event; the public prop takes no
                // arguments so a feature cannot accidentally depend on it.
                onSelect={() => item.onSelect()}
                className={cn(
                  'flex min-h-11 cursor-pointer select-none items-center gap-2.5 rounded-md px-3 text-sm outline-none',
                  'transition-colors duration-150 ease-out',
                  item.destructive
                    ? 'text-status-missed data-[highlighted]:bg-status-missed/10'
                    : 'text-ink data-[highlighted]:bg-raised',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                {item.icon ? <item.icon className="size-4 shrink-0" aria-hidden="true" /> : null}
                <span className="truncate">{item.label}</span>
              </DropdownMenuPrimitive.Item>
            </Fragment>
          ))}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

/** Escape hatches for a menu that needs checkboxes, submenus or custom content. */
export const DropdownMenuPrimitives = DropdownMenuPrimitive;
